<script setup lang="ts">
/**
 * Шаблон «Фото на всю карточку» — название и цена лежат на фото под градиентом.
 * Витринный вид: работает только на качественных фото, поэтому не дефолт.
 *
 * Соотношение сторон здесь задаёт сама карточка (3:4), а не настройка фото —
 * иначе текст поверх кадра плавал бы по высоте от товара к товару.
 */
import { PRODUCT_CARD_PROP_DEFAULTS, type ProductCardEmits, type ProductCardProps } from './cardContract';
import { getCardDescription } from '@entities/product';

const props = withDefaults(defineProps<ProductCardProps>(), PRODUCT_CARD_PROP_DEFAULTS);
const emit = defineEmits<ProductCardEmits>();

const description = computed(() => getCardDescription(props.item));
</script>

<template>
  <div
    class="pcard pcard--overlay"
    :class="[`pcard--surface-${config.surface}`]"
    :data-testid="`product-card-${item.id}`"
    @click="emit('open')"
  >
    <div class="pcard__media-wrap">
      <ProductCardMedia :item="item" fill :placeholder="placeholder" :priority="priority" />
    </div>
    <div class="pcard__scrim" />

    <ProductBadges
      v-if="config.showBadges"
      :tags="item.tags"
      :teg="item.teg"
      size="sm"
      :max-visible="3"
      class-name="pcard__badges"
    />

    <ProductCardFavorite
      v-if="favoriteEnabled"
      class="pcard__favorite"
      on-dark
      :active="!!item.is_favorite"
      :animation="favoriteAnimation"
      @click="emit('favorite')"
    />

    <div class="pcard__body">
      <h3 class="pcard__title">
        <!-- Ссылка, а не текст: карточка открывается модалкой, и без href у блюда
             не было ни одного адреса, по которому его мог бы найти поиск. Клик
             гасим — поведение витрины остаётся прежним, но Ctrl+клик и
             «скопировать адрес ссылки» теперь работают. -->
        <a v-if="href" :href="href" class="pcard__title-link" @click.prevent="emit('open')">{{ item.name }}</a>
        <template v-else>{{ item.name }}</template>
      </h3>
      <p v-if="config.showDescription && description" class="pcard__description">{{ description }}</p>

      <div class="pcard__bottom">
        <ProductCardPrice :item="item" :variant="config.priceStyle" :on-dark="config.priceStyle === 'plain'" />
        <ProductCardAction
          :in-cart="inCart"
          :cart-quantity="cartQuantity"
          :variant="config.actionStyle"
          :data-testid="inCart ? `product-card-incart-${item.id}` : `product-card-add-${item.id}`"
          @click="emit('action')"
        />
      </div>
    </div>
  </div>
</template>
